import React, { useEffect, useMemo, useState } from "react";
import { db } from "./firebase";
import { collection, onSnapshot, orderBy, query, where } from "firebase/firestore";
import AsyncCollectionState from "./components/AsyncCollectionState";

function UpdateCard({ u }) {
  const preview = (u.message || u.content || "").slice(0, 160);
  const full = u.message || u.content || "";
  const needsMore = full.length > preview.length;

  return (
    <div className="border border-white/10 rounded-xl p-5 bg-white/5">
      <p className="font-semibold">{u.title || "Update"}</p>
      {u.date && <p className="text-xs text-gray-400 mt-1">{u.date}</p>}

      {!needsMore ? (
        <p className="text-sm text-gray-300 mt-2 whitespace-pre-wrap">{full}</p>
      ) : (
        <details className="mt-2">
          <summary className="cursor-pointer text-sm text-gray-300">
            {preview}
            <span className="text-xs text-gray-400"> … Read more</span>
          </summary>
          <p className="text-sm text-gray-300 mt-2 whitespace-pre-wrap">{full}</p>
        </details>
      )}
    </div>
  );
}

export default function FoodBank() {
  const [updates, setUpdates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState("");
  const [loadAttempt, setLoadAttempt] = useState(0);

  useEffect(() => {
    setLoading(true);
    setLoadError("");
    const ref = collection(db, "foodBankUpdates");
    const q = query(ref, where("status", "==", "approved"), orderBy("createdAt", "desc"));
    const unsub = onSnapshot(
      q,
      (snap) => {
        setUpdates(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
        setLoading(false);
      },
      (error) => {
        console.error("Unable to load approved food-bank updates", error);
        setLoadError("Food-support updates are temporarily unavailable. Please try again.");
        setLoading(false);
      }
    );
    return () => unsub();
  }, [loadAttempt]);

  const latest = useMemo(() => updates.slice(0, 3), [updates]);

  return (
    <div className="text-white max-w-5xl mx-auto">
      <h1 className="text-3xl font-bold mb-3">UUN Food Bank</h1>
      <p className="text-gray-300 mb-6">
        A community-led food support project on the Barkantine Estate, powered by volunteers and local partners.
        We collect reduced-price and surplus food and redistribute it to residents who need a hand.
      </p>

      <div className="space-y-4">
        <details className="bg-black/40 border border-white/10 rounded-2xl p-6" open>
          <summary className="cursor-pointer">
            <span className="text-xl font-semibold">Need support?</span>
            <span className="block text-sm text-gray-300 mt-1">
              Quick info on getting help — and how to contact us.
            </span>
          </summary>

          <div className="mt-4">
            <p className="text-sm text-gray-300">
              If you or your family could use food support, contact us and we’ll reply with the latest
              pick-up times, availability and how the service works.
            </p>

            <div className="mt-4">
              <a
                className="inline-flex items-center justify-center px-4 py-2 rounded-lg bg-[#E63946] text-white text-sm font-semibold hover:bg-[#f25561]"
                href="mailto:info@urbanunitynetwork.com?subject=UUN%20Food%20Bank%20Support"
              >
                Email for support
              </a>
            </div>

            <p className="text-xs text-gray-500 mt-3">
              Privacy note: please don’t include sensitive personal details in your first message. We’ll guide you from there.
            </p>
          </div>
        </details>

        <details className="bg-black/40 border border-white/10 rounded-2xl p-6">
          <summary className="cursor-pointer">
            <span className="text-xl font-semibold">Want to help?</span>
            <span className="block text-sm text-gray-300 mt-1">
              Volunteer, donate, or sponsor to keep the project running.
            </span>
          </summary>

          <div className="mt-4">
            <p className="text-sm text-gray-300">
              We welcome volunteers, community groups, and businesses who can donate food, time, space, or funding.
              Even small regular support makes a big difference.
            </p>

            <div className="mt-4 flex flex-wrap gap-2">
              <a
                className="inline-flex items-center justify-center px-4 py-2 rounded-lg bg-blue-600 text-white text-sm font-semibold hover:bg-blue-500"
                href="mailto:info@urbanunitynetwork.com?subject=Volunteer%20-%20UUN%20Food%20Bank"
              >
                Volunteer
              </a>
              <a
                className="inline-flex items-center justify-center px-4 py-2 rounded-lg bg-white/10 text-white text-sm font-semibold hover:bg-white/20"
                href="mailto:info@urbanunitynetwork.com?subject=Donate%20-%20UUN%20Food%20Bank"
              >
                Donate / Sponsor
              </a>
            </div>
          </div>
        </details>

        <section className="bg-black/40 border border-white/10 rounded-2xl p-6">
          <h2 className="text-xl font-semibold mb-2">Latest updates</h2>
          <p className="text-sm text-gray-300 mb-4">
            We post updates here as they’re confirmed. (Showing the latest 3.)
          </p>

          <AsyncCollectionState
            loading={loading}
            error={loadError}
            isEmpty={latest.length === 0}
            emptyMessage="No updates yet. Check back soon."
            onRetry={() => setLoadAttempt((attempt) => attempt + 1)}
          >
            <div className="space-y-3">
              {latest.map((u) => (
                <UpdateCard key={u.id} u={u} />
              ))}
            </div>
          </AsyncCollectionState>

          {updates.length > 3 && (
            <details className="mt-4">
              <summary className="cursor-pointer text-sm text-gray-300">
                View all updates <span className="text-xs text-gray-400">({updates.length})</span>
              </summary>
              <div className="mt-3 space-y-3">
                {updates.slice(3).map((u) => (
                  <UpdateCard key={u.id} u={u} />
                ))}
              </div>
            </details>
          )}

          <p className="text-xs text-gray-500 mt-3">
            Updates are posted by the UUN team and approved before publishing.
          </p>
        </section>

        <details className="bg-black/40 border border-white/10 rounded-2xl p-6">
          <summary className="cursor-pointer">
            <span className="text-xl font-semibold">How it runs</span>
            <span className="block text-sm text-gray-300 mt-1">A simple overview of the process.</span>
          </summary>

          <ul className="list-disc pl-5 text-sm text-gray-300 space-y-2 mt-4">
            <li>Volunteers collect reduced-price and surplus food from local retailers.</li>
            <li>Food is sorted and distributed to residents and families.</li>
            <li>We keep this community-focused, respectful, and as accessible as possible.</li>
          </ul>
        </details>
      </div>
    </div>
  );
}
